import React, { useEffect, useState } from 'react';
import { FadeIn } from './ui/FadeIn';
import { CTAButton } from './ui/CTAButton';
import { Clock, Zap, ShieldCheck, RotateCcw, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
function useCountdown(minutes: number) {
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = sessionStorage.getItem('pricing_countdown_end');
    if (saved) {
      const remaining = Math.max(0, Math.floor((parseInt(saved) - Date.now()) / 1000));
      return remaining;
    }
    const end = Date.now() + minutes * 60 * 1000;
    sessionStorage.setItem('pricing_countdown_end', end.toString());
    return minutes * 60;
  });
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);
  const hours = Math.floor(timeLeft / 3600);
  const minutes_left = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  return {
    hours,
    minutes: minutes_left,
    seconds,
    expired: timeLeft <= 0
  };
}
function TimerDigit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-154">
      <div className="bg-charcoal text-white font-bold text-2xl md:text-3xl w-14 md:w-16 h-14 md:h-16 rounded-xl flex items-center justify-center shadow-lg" data-id="element-155">
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-xs text-warmgray mt-1.5 uppercase tracking-wider" data-id="element-156">
        {label}
      </span>
    </div>;
}
export function PricingSection() {
  const {
    hours,
    minutes,
    seconds,
    expired
  } = useCountdown(15);
  return <section id="pricing" className="py-16 md:py-24 px-6 relative" data-id="element-157">
      <div className="max-w-3xl mx-auto text-center" data-id="element-158">
        <FadeIn data-id="element-159">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal mb-4" data-id="element-160">
            Recupera el control de tu cuerpo hoy mismo
          </h2>
          <p className="text-xl text-warmgray mb-12 font-playfair italic" data-id="element-161">
            No esperes a que la ropa te deje de quedar para tomar acción.
          </p>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-162">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-terracotta/20 relative overflow-hidden" data-id="element-163">
            {/* Decorative ribbon/accent */}
            <div className="absolute top-0 left-0 w-full h-2 bg-terracotta" data-id="element-164"></div>

            {/* Urgency badge */}
            <motion.div animate={{
            scale: [1, 1.03, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-red-50 border border-red-200 mb-6" data-id="element-165">
              <Zap className="w-4 h-4 text-red-500 fill-red-500" data-id="element-166" />
              <span className="text-red-600 font-bold text-sm tracking-wide uppercase" data-id="element-167">
                Oferta por tiempo limitado
              </span>
            </motion.div>

            {/* Countdown timer */}
            {!expired && <div className="mb-8" data-id="element-168">
                <div className="flex items-center justify-center gap-1.5 mb-3" data-id="element-169">
                  <Clock className="w-4 h-4 text-terracotta" data-id="element-170" />
                  <span className="text-sm font-semibold text-charcoal" data-id="element-171">
                    Esta oferta expira en:
                  </span>
                </div>
                <div className="flex items-center justify-center gap-3" data-id="element-172">
                  {hours > 0 && <>
                      <TimerDigit value={hours} label="Hrs" data-id="element-173" />
                      <span className="text-2xl font-bold text-charcoal/30 -mt-5" data-id="element-174">
                        :
                      </span>
                    </>}
                  <TimerDigit value={minutes} label="Min" data-id="element-175" />
                  <span className="text-2xl font-bold text-charcoal/30 -mt-5" data-id="element-176">
                    :
                  </span>
                  <TimerDigit value={seconds} label="Seg" data-id="element-177" />
                </div>
              </div>}

            <div className="mb-8" data-id="element-178">
              <span className="inline-block py-1.5 px-4 rounded-full bg-sage/10 text-sage font-semibold text-sm tracking-widest uppercase mb-6" data-id="element-179">
                Pago único · Precio de lanzamiento
              </span>

              <div className="flex items-center justify-center gap-4 mb-1" data-id="element-180">
                <span className="text-2xl text-warmgray/40 line-through font-medium" data-id="element-181">
                  $27
                </span>
                <span className="text-6xl md:text-7xl font-bold text-terracotta" data-id="element-182">
                  $6<span className="text-5xl md:text-6xl" data-id="element-183">.36</span>
                </span>
              </div>
              <p className="text-sm text-sage font-semibold" data-id="element-184">
                Ahorras más del 76% — solo por hoy
              </p>
            </div>

            <CTAButton href="https://www.viventispace.com/cart/45988395614396:1" className="w-full md:w-auto mb-6 px-10 py-5 text-xl" data-id="element-185">
              DESCARGA MI PROTOCOLO 48h AHORA
            </CTAButton>

            <div className="space-y-2 text-sm text-warmgray" data-id="element-186">
              <p className="flex items-center justify-center gap-2" data-id="element-187">
                <span className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-188"></span>
                Acceso instantáneo en PDF tras el pago.
              </p>
              <p className="flex items-center justify-center gap-2" data-id="element-189">
                <span className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-190"></span>
                Garantía de satisfacción de 7 días.
              </p>
            </div>

            {/* Trust badges */}
            <div className="mt-8 pt-6 border-t border-sage/10 grid grid-cols-3 gap-4" data-id="element-191">
              <div className="flex flex-col items-center gap-2" data-id="element-192">
                <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center" data-id="element-193">
                  <ShieldCheck className="w-5 h-5 text-sage" data-id="element-194" />
                </div>
                <span className="text-xs text-warmgray font-medium text-center" data-id="element-195">
                  Pago 100% Seguro
                </span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-196">
                <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center" data-id="element-197">
                  <RotateCcw className="w-5 h-5 text-sage" data-id="element-198" />
                </div>
                <span className="text-xs text-warmgray font-medium text-center" data-id="element-199">
                  Garantía 7 Días
                </span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-200">
                <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center" data-id="element-201">
                  <CreditCard className="w-5 h-5 text-sage" data-id="element-202" />
                </div>
                <span className="text-xs text-warmgray font-medium text-center" data-id="element-203">
                  Sin Suscripción
                </span>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}